import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { saveLocalPlayer, localEntries } from "../../shared/local-players";
import { useIsTouch } from "../../shared/useResponsive";
import type { PlayOptions } from "../../online/net";
import RemoteGame, { type OnlineSession } from "../play/game/RemoteGame";
import { LevelTag, Seats } from "./LobbyRoom";
import { B, Chat, RoomsPage, Stepper, useLand } from "./RoomsUI";
import { START, applyFlavour, cleanRules, configOf, rollShape, type FlavourId, type Rules } from "./roomRules";
import { RuleRows, viewOfConfig } from "./RuleRows";
import { AddPlayer, ChatCard, InviteCard, KindMenu, Panel, RoomLayout, SKILL_OF, levelOf, type Kind } from "./RoomParts";
import { saveLocalRules } from "./localSetupStore";
import rp from "./roomPage.module.scss";
import { useMatchSetup, MAX_COUCH_SCHEMES } from "./useMatchSetup";
import { useNarrow } from "./useNarrow";
import s from "./rooms.module.scss";
import { ON_PORTAL } from "../../shared/portal-env";
import { portalRoom } from "../../shared/portal";

interface Props {
  onBack?: () => void;
}

// What the page that created the room hands over (router state): whether it is
// listed under open rooms, and the flavour it starts from.
interface Handoff { public?: boolean; flavour?: FlavourId; rules?: Rules }

// The host's rules survive a reload of the tab (the room itself is rebuilt by
// whoever opens it first, so the rules have to come back with the host).
const storeKey = (code: string) => "ar.room.host." + code;
function loadHost(code: string): { rules: Rules; pub: boolean; name: string } | null {
  try {
    const raw = sessionStorage.getItem(storeKey(code));
    const v = raw ? JSON.parse(raw) : null;
    return v && v.rules ? { rules: cleanRules(v.rules), pub: !!v.pub, name: typeof v.name === "string" ? v.name : "" } : null;
  } catch { return null; }
}
function saveHost(code: string, rules: Rules, pub: boolean, name: string): void {
  try { sessionStorage.setItem(storeKey(code), JSON.stringify({ rules, pub, name })); } catch { /* private mode */ }
}

function Connecting({ code }: { code: string }) {
  return (
    <div className={s.connecting}>
      <i aria-hidden="true" />
      <small>Joining room</small>
      <b>{code}</b>
    </div>
  );
}

// How many play from this device in room `code`, kept for the tab so a reload
// of the room does not ask again.
const whoKey = (code: string) => "ar.room.who." + code;
function savedWho(code: string): number | null {
  try { const n = Number(sessionStorage.getItem(whoKey(code))); return n >= 1 ? n : null; } catch { return null; }
}

// /lobby/CODE. Straight into the room: someone else on this keyboard is added
// in the room itself (Add player). A reload of the tab keeps however many were
// playing from this device then.
export default function CustomLobbyPage({ onBack }: Props) {
  const params = useParams();
  const code = (params.code || "").toUpperCase().slice(0, 8);
  const touch = useIsTouch();
  const [count] = useState(() => (touch ? 1 : savedWho(code) ?? 1));
  return <Room onBack={onBack} count={count} />;
}

// The live room at /lobby/CODE. The room goes live the moment this opens; the
// server decides who is host (first to connect). The host can change the rules
// and start; anyone opening the shared link joins as a guest and waits. Once
// the host starts, the match takes over full-screen.
function Room({ onBack, count }: Props & { count: number }) {
  const params = useParams();
  const location = useLocation();
  const [search] = useSearchParams();
  const code = (params.code || "").toUpperCase().slice(0, 8);
  const handoff = (location.state || {}) as Handoff;
  const saved = useMemo(() => loadHost(code), [code]);
  const land = useLand();
  const touch = useIsTouch();
  // The chat is a column on a wide PC window; on a phone or a narrow window it is
  // a panel behind the chat button in the header.
  // Under this width the third column (the invite and the chat) does not fit:
  // it opens from a header button instead, as a panel on the right.
  const narrow = useNarrow(1151);
  const chatPanel = land || narrow;
  // Only the couch players come from the setup hook here; the rules are `rules`.
  const [extras] = useState(() => localEntries(count).slice(1));
  const couch = useMatchSetup(true, extras);
  const [rules, setRules] = useState<Rules>(() => saved?.rules ?? (handoff.rules ? cleanRules(handoff.rules) : applyFlavour(START, handoff.flavour && handoff.flavour !== "custom" ? handoff.flavour : "classic")));
  const [pub, setPub] = useState<boolean>(() => saved?.pub ?? (typeof handoff.public === "boolean" ? handoff.public : search.get("kind") === "public"));
  const navigate = useNavigate();
  // What the host calls the room (empty: "<host>'s room"), typed into the title.
  const [roomName, setRoomName] = useState<string>(() => saved?.name ?? "");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeen, setChatSeen] = useState(0);
  const [session, setSession] = useState<OnlineSession | null>(null);
  const sessionRef = useRef<OnlineSession | null>(null);
  sessionRef.current = session;

  const isHost = !!session?.isHost;
  const phase = session?.phase ?? "connecting";
  const inGame = phase !== "connecting" && phase !== "waiting"; // playing/countdown/over/disconnected
  const connecting = phase === "connecting"; // haven't learned host vs guest yet

  // Seat the couch human(s) into the shared store BEFORE RemoteGame mounts + reads
  // it (a render-phase one-shot), so the first `play` seats them.
  useState(() => {
    const humans = couch.players.filter((p) => p.type === "human").slice(0, 2);
    humans.forEach((p, i) => saveLocalPlayer(i, { name: p.name, colorId: p.colorId, left: p.left, right: p.right }));
    return true;
  });

  // Play options + config captured once (sent on connect). Everyone sends it; the
  // server only honors the host's (first joiner). Edits propagate via updateConfig.
  const hostConfig = useMemo(() => ({ ...configOf(rules), public: pub, name: roomName }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const hostOptions: PlayOptions = useMemo(() => ({ map: hostConfig.map, name: couch.players[0]?.name, colorId: couch.players[0]?.colorId }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Host: push edited settings to the live room (server ignores non-host).
  useEffect(() => {
    sessionRef.current?.updateConfig({ ...configOf(rules), public: pub, name: roomName });
  }, [rules, pub, roomName]);
  useEffect(() => {
    if (isHost) saveHost(code, rules, pub, roomName);
  }, [isHost, code, rules, pub, roomName]);

  // The couch players on this device (name/colour/keys), passed to RemoteGame so it
  // seats + steers all of them (not just the two store slots).
  const mine = useMemo(() => couch.players.filter((p) => p.type === "human"), [couch.players]);
  const couchPlayers = useMemo(
    () => mine.map((p) => ({ name: p.name, colorId: p.colorId, left: p.left, right: p.right, flag: p.flag, victoryAnim: p.victoryAnim ?? null, headStyle: p.headStyle ?? null })),
    [mine]
  );

  // A game site's build: the site knows which room you are in, so your friends
  // can join it from the site (not while its match is on). Leaving the room
  // tells it you left. (shared/portal.ts)
  useEffect(() => {
    if (ON_PORTAL && !connecting) portalRoom(code, !inGame);
  }, [code, connecting, inGame]);
  useEffect(() => () => { if (ON_PORTAL) portalRoom(null, false); }, []);

  // how many play from this device, for a reload of the tab
  useEffect(() => { try { sessionStorage.setItem(whoKey(code), String(Math.max(1, mine.length))); } catch { /* private mode */ } }, [code, mine.length]);

  // Couch players changed: reseat all of them on the server; keep player 1 & 2 in
  // the shared store (2 slots) so the local setup screen agrees on them.
  useEffect(() => {
    mine.slice(0, 2).forEach((p, i) => saveLocalPlayer(i, { name: p.name, colorId: p.colorId, left: p.left, right: p.right }));
    sessionRef.current?.setLocals(mine.map((p) => ({ name: p.name, colorId: p.colorId, flag: p.flag, victoryAnim: p.victoryAnim ?? null, headStyle: p.headStyle ?? null })));
  }, [mine]);

  // Leaving closes the room (host) / leaves it (guest). Warn the host if remote
  // players have already joined.
  function handleLeave() {
    const youIds = session?.youIds ?? [];
    const joined = (session?.players ?? []).filter((p: any) => p.type === "human" && !youIds.includes(p.id)).length;
    if (isHost && joined > 0 && typeof window !== "undefined" && !window.confirm(`${joined} player${joined > 1 ? "s have" : " has"} joined. Leaving will close the room for everyone. Leave anyway?`)) return;
    onBack?.();
  }

  // Stable so it doesn't defeat RemoteGame's React.memo (a fresh arrow each render would
  // re-render the live canvas on every lobby/chat/session update).
  const leaveMatch = useCallback(() => onBack?.(), [onBack]);

  const players: any[] = session?.players ?? [];
  const youIds = session?.youIds ?? [];
  const cap = session?.maxPlayers || rules.players;
  const full = players.length >= cap;
  // The host's seat: ours when we host, else the first player in (the server
  // lists the seats in the order they joined, and the host opened the room).
  const hostSeat = isHost ? players.find((p) => youIds.includes(p.id)) : players.find((p) => p.type === "human");
  const hostName = hostSeat?.name || "";

  // "Surprise me" draws the map when the match starts.
  function start() {
    if (!isHost) return;
    if (rules.shape === "surprise") {
      const next = { ...rules, rolled: rollShape() };
      setRules(next);
      session?.updateConfig({ ...configOf(next), public: pub, name: roomName });
    }
    session?.start();
  }

  // A guest reads the rules off the host's live config, so the rows follow every
  // change the host makes.
  const guestConfig = { ...(session?.config || {}), maxPlayers: session?.maxPlayers };

  const chatCount = session?.chat?.length ?? 0;
  useEffect(() => {
    if (chatOpen || !chatPanel) setChatSeen(chatCount);
  }, [chatOpen, chatPanel, chatCount]);

  // Who can join, from the header. Local leaves the room for the local game,
  // taking the rules along (a host with friends in the room is asked first).
  const kind: Kind = pub ? "public" : "private";
  function pickKind(k: Kind) {
    if (k === "local") {
      const joined = players.filter((p: any) => p.type === "human" && !youIds.includes(p.id)).length;
      if (joined > 0 && typeof window !== "undefined" && !window.confirm(`${joined} player${joined > 1 ? "s are" : " is"} in the room. Playing locally closes it for everyone. Go on?`)) return;
      saveLocalRules(rules);
      navigate("/local");
      return;
    }
    setPub(k === "public");
  }

  const fillLevel = rules.fill ? levelOf(rules.fillSkill) : null;
  const couchFull = mine.length >= MAX_COUCH_SCHEMES;
  const playersCol = (<>
    {(isHost || !touch) && (
      <Panel className={rp.playersTop}>
        {isHost && (
          <div className={rp.maxRow}>
            <b>Max players</b>
            <Stepper small label="max players" value={rules.players} onChange={(d) => setRules((x) => ({ ...x, players: Math.max(Math.max(2, players.length), Math.min(40, x.players + d)) }))} min={rules.players <= Math.max(2, players.length)} max={rules.players >= 40} />
          </div>
        )}
        <AddPlayer
          local={touch ? undefined : { onAdd: () => couch.addLocalPlayer(MAX_COUCH_SCHEMES), keys: couch.nextKeys.left ? `${couch.nextKeys.left} / ${couch.nextKeys.right}` : null, off: full ? "The room is full" : couchFull ? `${MAX_COUCH_SCHEMES} players is the most on one keyboard` : null }}
          bot={isHost ? { onAdd: (l) => session?.addBot(SKILL_OF[l]), off: full } : undefined}
          fill={isHost ? { level: fillLevel, onPick: (l) => setRules((x) => ({ ...x, fill: !!l, fillSkill: l ? SKILL_OF[l] : x.fillSkill })) } : undefined}
          full={full ? { text: cap === 2 ? "Both seats are taken" : `All ${cap} seats are taken`, onMore: isHost && rules.players < 40 ? () => setRules((x) => ({ ...x, players: Math.min(40, x.players + 1) })) : undefined } : null}
        />
      </Panel>
    )}
    <Panel className={rp.seatsCol}>
      <Seats bare noHead={land}
        session={session} mine={mine} edit={couch} cap={cap} hostId={hostSeat?.id ?? null} keys={!touch} single
        emptyLabel={isHost && fillLevel ? <LevelTag level={fillLevel} dim /> : undefined}
      />
    </Panel>
  </>);
  const rulesCol = isHost
    ? <RuleRows rules={rules} setRules={setRules} touch={touch} minSeats={Math.max(2, players.length)} />
    : <RuleRows view={viewOfConfig(guestConfig, cap)} />;
  const startBtn = isHost
    ? <B className={rp.start} onClick={start} disabled={players.length < 1}>Start game</B>
    : <div className={rp.waiting}><i /><i /><i />Waiting for the host</div>;
  const side = (<>
    <InviteCard code={code} />
    {/* (no chat on a game site's build: there is no word filter yet) */}
    {!ON_PORTAL && <ChatCard chat={session?.chat} onSend={session?.sendChat} />}
  </>);

  const kindMenu = connecting ? null : <KindMenu kind={kind} onPick={isHost ? pickKind : undefined} offer={touch ? ["private", "public"] : undefined} right={land} />;
  // (a phone: who can join sits in the screen's top right corner)
  const right = land ? kindMenu : chatPanel && !connecting ? (
    <B className={s.headBtn} onClick={() => setChatOpen((c) => !c)} aria-label="Invite and chat"><Chat />{chatCount > chatSeen && !ON_PORTAL && <i className={s.dot} />}</B>
  ) : null;
  // The title: the room's name. The host types it in place (empty shows the
  // usual "<host>'s room"); everyone else reads what the host set.
  const defaultName = hostName ? `${hostName}'s room` : "Game room";
  const title = connecting ? "Game room" : isHost
    ? <input className={s.titleIn} value={roomName} onChange={(e) => setRoomName(e.target.value.slice(0, 28))} placeholder={defaultName} aria-label="Room name" spellCheck={false} />
    : (session?.roomName || defaultName);

  return (
    <>
      {/* The live match goes full-screen over the whole app (nav + footer), like
          local / public play. It stays mounted the whole time (hidden until the
          match starts) so the live session never drops. */}
      {/* Outer = the full-viewport cover and scroller (scrollbar on the browser's edge);
          inner = the centred, width-capped content column. See OnlineMatchPage. As one
          box with no overflow, a match-over page taller than the window could not be
          scrolled here at all. */}
      <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "#060b18", overflowY: "auto", display: inGame ? "block" : "none" }}>
        <div className="page-main page-main-game">
          <RemoteGame roomId={"lobby:" + code} options={hostOptions} lobbyConfig={hostConfig} localPlayers={couchPlayers} onSession={setSession} onLeave={leaveMatch} />
        </div>
      </div>
      {!inGame && (
        <RoomsPage
          wide
          title={title}
          after={land ? null : kindMenu}
          sub={connecting ? "" : isHost ? "You are the host" : "You are in"}
          onBack={handleLeave}
          backLabel="Leave"
          right={right}
          stageKey={connecting ? "connecting" : "room"}
          over={chatPanel && !land && chatOpen ? (
            <div className={rp.drawerScrim} onClick={() => setChatOpen(false)}>
              <div className={rp.drawer} onClick={(e) => e.stopPropagation()}>{side}</div>
            </div>
          ) : null}
        >
          {connecting ? <Connecting code={code} /> : (
            <RoomLayout land={land} players={playersCol} rules={rulesCol} start={startBtn} side={narrow && !land ? undefined : side} sideDot={land && chatCount > chatSeen && !ON_PORTAL} firstTab={ON_PORTAL && isHost ? "side" : "rules"} />
          )}
        </RoomsPage>
      )}
    </>
  );
}
